import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Table, Input, Space, Select, DatePicker, Tag, Button, notification } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { getOutOfStockHistory } from '../../services/outofstockservice';
import { HistoryItem, PagedResult } from '../../types/outofstocktypes';
import { UserDropdownDto } from '../../types/userypes';
import { getDropdownUsers } from '../../services/userservice';
import { debounce } from 'lodash';
import dayjs from 'dayjs';
import { StoreDropdownDto } from '../../types/storetypes';
import { getAllStoresForDropdown } from '../../services/storeserivce';

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface HistoryTabProps {
    activeTab: string;
}

type ItemType = 'Menu' | 'Recipe' | undefined;
type ActionType = 'OutOfStock' | 'Restock' | undefined;

const HistoryTab: React.FC<HistoryTabProps> = ({ activeTab }) => {
    const [data, setData] = useState<HistoryItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ['5', '10', '20', '50', '100'],
        showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`,
    });

    // Filters
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [itemType, setItemType] = useState<ItemType>(undefined);
    const [action, setAction] = useState<ActionType>(undefined);
    const [storeNumber, setStoreNumber] = useState<string | undefined>(undefined);
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
    const [stores, setStores] = useState<StoreDropdownDto[]>([]);
    const [users, setUsers] = useState<UserDropdownDto[]>([]);
    const [userGuid, setUserGuid] = useState<string | undefined>(undefined);

    // --- Data fetcher (stable) ---
    const fetchHistory = useCallback(
        async (
            page: number,
            pageSize: number,
            itemName?: string,
            itemType?: 'Menu' | 'Recipe',
            action?: 'OutOfStock' | 'Restock',
            storeNumber?: string,
            startDate?: string,
            endDate?: string,
            userGuid?: string
        ) => {
            setLoading(true);
            try {
                const response = await getOutOfStockHistory(
                    page,
                    pageSize,
                    undefined, // menuGuid
                    undefined, // storeGuid
                    itemName,
                    storeNumber,
                    action,
                    itemType,
                    startDate,
                    endDate,
                    userGuid
                );
                const result: PagedResult<HistoryItem> = response.data;

                setData(result.items);

                // Only update pagination when values actually change to avoid unnecessary re-renders
                setPagination(prev => {
                    const next = {
                        ...prev,
                        current: result.pageNumber,
                        pageSize: result.pageSize,
                        total: result.totalCount,
                    };
                    if (
                        prev.current === next.current &&
                        prev.pageSize === next.pageSize &&
                        prev.total === next.total
                    ) {
                        return prev;
                    }
                    return next;
                });
            } catch (error: any) {
                notification.error({
                    message: 'Failed to load history',
                    description: error?.message || 'An unexpected error occurred while fetching history.',
                });
            } finally {
                setLoading(false);
            }
        },
        []
    );

    // Debounced version + cleanup
    const debouncedFetch = useMemo(() => debounce(fetchHistory, 300), [fetchHistory]);

    useEffect(() => {
        return () => {
            debouncedFetch.cancel();
        };
    }, [debouncedFetch]);

    // --- Bootstrap dropdowns on mount ---
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await getDropdownUsers();
                setUsers(response.data);
            } catch (error: any) {
                notification.error({
                    message: 'Failed to load users',
                    description: error?.message || 'Could not fetch user list.',
                });
            }
        };
        fetchUsers();
    }, []);

    useEffect(() => {
        const fetchStores = async () => {
            try {
                const response = await getAllStoresForDropdown();
                setStores(response.data);
            } catch (error: any) {
                notification.error({
                    message: 'Failed to load stores',
                    description: error?.message || 'Could not fetch store list.',
                });
            }
        };
        fetchStores();
    }, []);

    // --- Single source of truth for fetching ---
    // Fires only when active tab is "history" and relevant inputs change.
    useEffect(() => {
        if (activeTab !== 'history') return;

        debouncedFetch(
            pagination.current!,
            pagination.pageSize!,
            searchTerm,
            itemType,
            action,
            storeNumber,
            dateRange ? dateRange[0].toISOString() : undefined,
            dateRange ? dateRange[1].toISOString() : undefined,
            userGuid
        );
    }, [
        activeTab,
        searchTerm,
        itemType,
        action,
        storeNumber,
        dateRange,
        userGuid,
        pagination.current,
        pagination.pageSize,
        debouncedFetch,
    ]);

    // Table pagination change: update state only; the effect above triggers fetch.
    const handleTableChange = useCallback((newPagination: TablePaginationConfig) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));
    }, []);

    // Clear filters: update state only; the effect above triggers fetch.
    const clearFilters = useCallback(() => {
        setSearchTerm('');
        setItemType(undefined);
        setAction(undefined);
        setStoreNumber(undefined);
        setUserGuid(undefined);
        setDateRange(null);

        setPagination(prev => ({
            ...prev,
            current: 1,
        }));
    }, []);

    const columns: ColumnsType<HistoryItem> = [
        {
            title: 'Date/Time',
            dataIndex: 'dateCreated',
            key: 'dateCreated',
            render: (val: string) => dayjs(val).format('DD MMM YYYY, HH:mm'),
        },
        { title: 'Item', dataIndex: 'itemName', key: 'itemName' },
        { title: 'Type', dataIndex: 'itemType', key: 'itemType' },
        { title: 'Store Number', dataIndex: 'storeNumber', key: 'storeNumber' },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (val: 'OutOfStock' | 'Restock') =>
                val === 'OutOfStock' ? <Tag color="red">{val}</Tag> : <Tag color="green">{val}</Tag>,
        },
        { title: 'Reason', dataIndex: 'reason', key: 'reason' },
        {
            title: 'Performed By',
            dataIndex: 'performedBy',
            key: 'performedBy',
            render: (val: string) => val || 'Unknown',
        },
    ];

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Space wrap>
                <Search
                    placeholder="Search by item name"
                    allowClear
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: 200 }}
                />
                <Select
                    placeholder="Filter by Type"
                    allowClear
                    value={itemType}
                    onChange={setItemType}
                    style={{ width: 150 }}
                >
                    <Option value="Menu">Menu</Option>
                    <Option value="Recipe">Recipe</Option>
                </Select>
                <Select
                    placeholder="Filter by Action"
                    allowClear
                    value={action}
                    onChange={setAction}
                    style={{ width: 150 }}
                >
                    <Option value="OutOfStock">OutOfStock</Option>
                    <Option value="Restock">Restock</Option>
                </Select>
                <Select
                    placeholder="Filter by Store Number"
                    allowClear
                    value={storeNumber}
                    onChange={setStoreNumber}
                    style={{ width: 200 }}
                    showSearch
                    optionFilterProp="children"
                >
                    {stores.map((s) => (
                        <Option key={s.storeGuid} value={s.storeNumber}>
                            {s.storeNumber}
                        </Option>
                    ))}
                </Select>
                <Select
                    placeholder="Filter by Performed By"
                    allowClear
                    value={userGuid}
                    onChange={setUserGuid}
                    style={{ width: 200 }}
                    showSearch
                    optionFilterProp="children"
                >
                    {users.map((u) => (
                        <Option key={u.guid} value={u.guid}>
                            {u.fullName}
                        </Option>
                    ))}
                </Select>
                <RangePicker
                    value={dateRange ?? undefined}
                    onChange={(range) => setDateRange(range as [dayjs.Dayjs, dayjs.Dayjs] | null)}
                    allowClear
                />
                <Button onClick={clearFilters}>Clear Filters</Button>
            </Space>

            <Table<HistoryItem>
                dataSource={data}
                columns={columns}
                pagination={pagination}
                loading={loading}
                rowKey="guid"
                onChange={handleTableChange}
            />
        </Space>
    );
};

export default HistoryTab;
